import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Clock, Lock, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Search, RefreshCw, User, ArrowDownToLine,
  SlidersHorizontal, X, Copy, Check
} from 'lucide-react';
import { paymentsAPI } from '../services/api';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Helper: normalise a raw API withdrawal record ────────────────────────────
function normalise(w) {
  return {
    ...w,
    payoutMethod:  w.payout_method  ?? w.payoutMethod  ?? '—',
    payoutDetails: w.payout_details ?? w.payoutDetails  ?? {},
    trader_name:   (w.trader_name ?? `${w.first_name ?? ''} ${w.last_name ?? ''}`.trim()) || '—',
    rejection_note: w.rejection_note ?? w.review_notes ?? null,
  };
}

// ─── Copy button ──────────────────────────────────────────────────────────────
function CopyBtn({ value, small }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copy"
      className={`inline-flex items-center justify-center rounded transition-all active:scale-90 flex-shrink-0 ${small ? 'w-4 h-4' : 'w-5 h-5'}`}
      style={{
        backgroundColor: copied
          ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)'
          : 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
        color: copied ? 'var(--accent-success)' : 'var(--accent-primary)',
      }}
    >
      {copied
        ? <Check className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        : <Copy className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} />}
    </button>
  );
}

// ─── Payout detail rows ───────────────────────────────────────────────────────
function PayoutDetailRows({ details, small }) {
  if (!details || !Object.keys(details).length) return <p style={{ color: 'var(--text-tertiary)' }}>—</p>;
  return (
    <div className="space-y-1">
      {Object.entries(details).map(([k, v]) => (
        <div key={k} className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`capitalize ${small ? 'text-[10px]' : 'text-xs'}`}
            style={{ color: 'var(--text-tertiary)' }}
          >
            {k.replace(/([A-Z])/g, ' $1').trim()}:
          </span>
          <span
            className={`font-mono break-all ${small ? 'text-[10px]' : 'text-xs'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {String(v)}
          </span>
          <CopyBtn value={v} small={small} />
        </div>
      ))}
    </div>
  );
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  pending:   { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', icon: Lock,            label: 'Pending',   varBg: 'var(--accent-warning)', varColor: 'var(--accent-warning)' },
  approved:  { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400',     icon: CheckCircle,     label: 'Approved',  varBg: 'var(--accent-info)',    varColor: 'var(--accent-info)'    },
  completed: { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',   icon: ArrowDownToLine, label: 'Completed', varBg: 'var(--accent-success)', varColor: 'var(--accent-success)' },
  rejected:  { bg: 'bg-red-100 dark:bg-red-900/30',      text: 'text-red-700 dark:text-red-400',       icon: XCircle,         label: 'Rejected',  varBg: 'var(--accent-danger)',  varColor: 'var(--accent-danger)'  },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      <Icon className="w-3.5 h-3.5" />{s.label}
    </span>
  );
}

function MobileStatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: `color-mix(in srgb, ${s.varBg} 15%, transparent)`,
        color: s.varColor,
        border: `1px solid color-mix(in srgb, ${s.varBg} 30%, transparent)`,
      }}
    >
      <Icon className="w-2.5 h-2.5" />{s.label}
    </span>
  );
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────
const TABS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected',  label: 'Rejected' },
];

// ─── Mobile withdrawal card ───────────────────────────────────────────────────
function WithdrawalCard({ w, expanded, onToggle, actionId, actionType, note, setNote, onOpenAction, onCancelAction, onSubmitAction, submitting }) {
  const isOpen   = expanded === w.id;
  const isAction = actionId === w.id;
  const canAct   = w.status === 'pending';

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
    >
      {/* Main row */}
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left" onClick={() => onToggle(w.id)}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)', color: 'var(--accent-danger)' }}
        >
          <User className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>
            {w.trader_name}
          </p>
          <p className="text-[11px] truncate capitalize" style={{ color: 'var(--text-tertiary)' }}>
            {w.payoutMethod} · {w.trader_email || w.account_id}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-sm font-black font-mono" style={{ color: 'var(--accent-danger)' }}>
            −{Number(w.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <ChevronDown
            className="w-4 h-4 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      </button>

      {/* Status badge row */}
      <div className="flex items-center gap-2 px-4 pb-3">
        <MobileStatusBadge status={w.status} />
        <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{w.currency}</span>
        {canAct && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)',
              color: 'var(--accent-warning)',
            }}
          >
            Awaiting action
          </span>
        )}
      </div>

      {/* Expanded detail */}
      {isOpen && (
        <div
          className="border-t px-4 py-3 space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Request ID</p>
              <div className="flex items-center gap-1">
                <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>#{w.id}</p>
                <CopyBtn value={w.id} small />
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Created</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {new Date(w.created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Method</p>
              <p className="text-xs font-semibold capitalize" style={{ color: 'var(--text-primary)' }}>{w.payoutMethod}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Amount</p>
              <p className="text-xs font-semibold font-mono" style={{ color: 'var(--accent-danger)' }}>
                −{Number(w.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} {w.currency}
              </p>
            </div>
            {w.reviewed_at && (
              <div>
                <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-tertiary)' }}>Reviewed</p>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                  {new Date(w.reviewed_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          {/* Payout details */}
          <div>
            <p className="text-[10px] uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Payout Details</p>
            <div
              className="rounded-xl p-3"
              style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
            >
              <PayoutDetailRows details={w.payoutDetails} small />
            </div>
          </div>

          {/* Rejection note */}
          {w.status === 'rejected' && (
            <div
              className="rounded-xl p-3"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, transparent)',
              }}
            >
              <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--accent-danger)' }}>Rejection</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Funds returned to trader's balance.</p>
              {w.rejection_note && (
                <p className="text-xs italic mt-0.5" style={{ color: 'var(--accent-danger)' }}>{w.rejection_note}</p>
              )}
            </div>
          )}

          {/* Action buttons */}
          {canAct && !isAction && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onOpenAction(w.id, 'approve')}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)',
                  color: 'var(--accent-success)',
                  border: '1px solid color-mix(in srgb, var(--accent-success) 25%, transparent)',
                }}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Approve
              </button>
              <button
                onClick={() => onOpenAction(w.id, 'reject')}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)',
                  color: 'var(--accent-danger)',
                  border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)',
                }}
              >
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
            </div>
          )}

          {/* Confirm panel */}
          {isAction && (
            <div
              className="rounded-xl p-3 space-y-2"
              style={{
                backgroundColor: actionType === 'approve'
                  ? 'color-mix(in srgb, var(--accent-success) 8%, transparent)'
                  : 'color-mix(in srgb, var(--accent-danger) 8%, transparent)',
                border: `1px solid color-mix(in srgb, ${actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)'} 20%, transparent)`,
              }}
            >
              <p className="text-xs font-semibold" style={{ color: actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                {actionType === 'approve'
                  ? "Confirm approval — funds will be released to the trader's payout details."
                  : "Confirm rejection — locked amount will be returned to the trader's balance."}
              </p>
              {actionType === 'reject' && (
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Reason for rejection (optional)…"
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border focus:outline-none text-xs"
                  style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={onSubmitAction}
                  disabled={submitting}
                  className="flex-1 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 transition-all active:scale-95"
                  style={{
                    backgroundColor: actionType === 'approve' ? 'var(--accent-success)' : 'var(--accent-danger)',
                    color: 'white',
                  }}
                >
                  {submitting
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : actionType === 'approve' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                  {submitting ? 'Processing…' : actionType === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button
                  onClick={onCancelAction}
                  className="px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
                  style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobile filter drawer ─────────────────────────────────────────────────────
function FilterDrawer({ activeTab, setActiveTab, search, setSearch, counts, onClose, onClear }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Filter Withdrawals</h3>
          <button onClick={onClear} className="text-xs font-semibold flex items-center gap-1"
            style={{ color: 'var(--accent-primary)' }}>
            <X className="w-3.5 h-3.5" />Reset
          </button>
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="ID, name or email…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>Status</label>
            <div className="space-y-2">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: activeTab === t.key ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--bg-tertiary)',
                    borderColor: activeTab === t.key ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)' : 'var(--border-color)',
                    color: activeTab === t.key ? 'var(--accent-primary)' : 'var(--text-primary)',
                  }}
                >
                  {t.label}
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{counts[t.key]}</span>
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mt-1"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function BrokerWithdrawalsPage() {
  const isMobile = useIsMobile();

  const [withdrawals, setWithdrawals] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [activeTab,   setActiveTab]   = useState('pending');
  const [search,      setSearch]      = useState('');
  const [expanded,    setExpanded]    = useState(null);
  const [actionId,    setActionId]    = useState(null);
  const [actionType,  setActionType]  = useState(null);
  const [note,        setNote]        = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentsAPI.getWithdrawals();
      // Normalise snake_case → camelCase + derived fields
      setWithdrawals((Array.isArray(data) ? data : data?.data ?? []).map(normalise));
    } catch (e) {
      setError(e.message || 'Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);

  const filtered = withdrawals.filter(w => {
    if (activeTab !== 'all' && w.status !== activeTab) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(w.id).includes(q) ||
        (w.trader_email || '').toLowerCase().includes(q) ||
        (w.trader_name  || '').toLowerCase().includes(q) ||
        (w.payoutMethod || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const openAction   = (id, type) => { setActionId(id); setActionType(type); setNote(''); };
  const cancelAction = () => { setActionId(null); setActionType(null); setNote(''); };

  const submitAction = async () => {
    setSubmitting(true);
    try {
      if (actionType === 'approve') await paymentsAPI.approveWithdrawal(actionId);
      else                           await paymentsAPI.rejectWithdrawal(actionId, { note });
      cancelAction();
      await fetchWithdrawals();
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpanded = (id) => setExpanded(e => e === id ? null : id);

  const counts = {};
  TABS.forEach(t => {
    counts[t.key] = t.key === 'all' ? withdrawals.length : withdrawals.filter(w => w.status === t.key).length;
  });

  const pendingCount = counts['pending'] || 0;

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
        </div>
      );
    }

    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* Hero */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-danger) 0%, color-mix(in srgb, var(--accent-danger) 60%, var(--accent-warning)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Withdrawal Requests
          </p>
          <p className="text-4xl font-black text-white leading-none font-mono">{counts.all}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Total withdrawals</p>

          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: 'Pending',   value: pendingCount },
              { label: 'Completed', value: counts.completed },
              { label: 'Rejected',  value: counts.rejected },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black text-white">{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex items-center gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search withdrawals…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold relative"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {TABS.find(t => t.key === activeTab)?.label}
            {pendingCount > 0 && activeTab === 'all' && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-white"
                style={{ backgroundColor: 'var(--accent-warning)' }}>
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={fetchWithdrawals}
            className="p-2 rounded-xl border"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Active tab chip */}
        {activeTab !== 'all' && (
          <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
              {TABS.find(t => t.key === activeTab)?.label}
              <button onClick={() => setActiveTab('all')}><X className="w-2.5 h-2.5" /></button>
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mx-4 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 12%, transparent)', color: 'var(--accent-danger)', border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {/* Result count */}
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {filtered.length} withdrawal{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No withdrawals match this filter</p>
            <button onClick={() => setActiveTab('all')} className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
              Show all
            </button>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {filtered.map(w => (
              <WithdrawalCard
                key={w.id}
                w={w}
                expanded={expanded}
                onToggle={toggleExpanded}
                actionId={actionId}
                actionType={actionType}
                note={note}
                setNote={setNote}
                onOpenAction={openAction}
                onCancelAction={cancelAction}
                onSubmitAction={submitAction}
                submitting={submitting}
              />
            ))}
          </div>
        )}

        {showFilters && (
          <FilterDrawer
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            search={search}
            setSearch={setSearch}
            counts={counts}
            onClose={() => setShowFilters(false)}
            onClear={() => { setActiveTab('all'); setSearch(''); }}
          />
        )}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--accent-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-[var(--text-primary)]">Withdrawal Requests</h1>
        <button onClick={fetchWithdrawals} className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-sm flex items-center gap-1.5 text-[var(--text-primary)] transition-smooth">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',     value: counts.all,       color: 'text-[var(--text-primary)]' },
          { label: 'Pending',   value: counts.pending,   color: 'text-yellow-600 dark:text-yellow-400' },
          { label: 'Completed', value: counts.completed, color: 'text-green-600 dark:text-green-400' },
          { label: 'Rejected',  value: counts.rejected,  color: 'text-red-600 dark:text-red-400' },
        ].map(c => (
          <div key={c.label} className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4">
            <p className="text-xs text-[var(--text-secondary)]">{c.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${activeTab === t.key ? 'bg-[var(--accent-primary)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'}`}>
              {t.label} <span className="opacity-70">({counts[t.key]})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
          <input type="text" placeholder="Search by ID, trader name or email…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-sm focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]" />
        </div>
      </div>

      <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-[var(--text-secondary)]">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm">No withdrawal requests match the current filter.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[2rem_1fr_1.4fr_1fr_1fr_1rem] items-center gap-3 px-4 py-2.5 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
              <span /><span>Trader</span><span>Payout Method</span><span>Amount</span><span>Status</span><span />
            </div>
            {filtered.map(w => {
              const isOpen   = expanded === w.id;
              const isAction = actionId === w.id;
              const canAct   = w.status === 'pending';
              return (
                <div key={w.id} className="border-b border-[var(--border-color)] last:border-0">
                  <button type="button" onClick={() => toggleExpanded(w.id)}
                    className="w-full grid grid-cols-[2rem_1fr_1.4fr_1fr_1fr_1rem] items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-secondary)] transition-smooth">
                    <span className="text-[var(--text-tertiary)]">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]">
                        <User className="w-3.5 h-3.5" />
                      </span>
                      <span className="min-w-0">
                        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{w.trader_name}</p>
                        <p className="text-xs text-[var(--text-secondary)] truncate">{w.trader_email || w.account_id}</p>
                      </span>
                    </span>
                    <span className="text-sm text-[var(--text-primary)]">
                      <span className="font-medium capitalize">{w.payoutMethod}</span>
                      {w.payoutDetails?.bankName && <span className="text-xs text-[var(--text-tertiary)] ml-1.5">· {w.payoutDetails.bankName}</span>}
                      {w.payoutDetails?.coin && <span className="text-xs text-[var(--text-tertiary)] ml-1.5">· {w.payoutDetails.coin} / {w.payoutDetails.network}</span>}
                    </span>
                    <span className="text-sm font-semibold text-red-600 dark:text-red-400 font-mono">
                      −{Number(w.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      <span className="ml-1 text-xs font-normal text-[var(--text-secondary)]">{w.currency}</span>
                    </span>
                    <span><StatusBadge status={w.status} /></span>
                    <span />
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3">

                        {/* Request details */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Request Details</p>
                          <div className="text-xs space-y-1.5 text-[var(--text-primary)]">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[var(--text-tertiary)]">ID:</span>
                              <span className="font-mono">{w.id}</span>
                              <CopyBtn value={w.id} small />
                            </div>
                            <p><span className="text-[var(--text-tertiary)]">Trader:</span> {w.trader_name}</p>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[var(--text-tertiary)]">Email:</span>
                              <span className="font-mono">{w.trader_email}</span>
                              {w.trader_email && <CopyBtn value={w.trader_email} small />}
                            </div>
                            <p><span className="text-[var(--text-tertiary)]">Created:</span> {new Date(w.created_at).toLocaleString()}</p>
                            {w.reviewed_at && <p><span className="text-[var(--text-tertiary)]">Reviewed:</span> {new Date(w.reviewed_at).toLocaleString()}</p>}
                          </div>
                        </div>

                        {/* Payout details */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Payout Details</p>
                          <div
                            className="rounded-lg p-3"
                            style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                          >
                            <p className="text-xs font-medium capitalize mb-2" style={{ color: 'var(--text-secondary)' }}>
                              Method: <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{w.payoutMethod}</span>
                            </p>
                            <PayoutDetailRows details={w.payoutDetails} />
                          </div>
                        </div>

                        {/* Status info */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Status Info</p>
                          <div className="text-xs space-y-1 text-[var(--text-primary)]">
                            <p className="flex items-center gap-2">
                              <span className="text-[var(--text-tertiary)]">Status:</span>
                              <StatusBadge status={w.status} />
                            </p>
                            <p><span className="text-[var(--text-tertiary)]">Amount:</span>{' '}
                              <span className="font-mono font-semibold text-red-600 dark:text-red-400">
                                −{Number(w.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} {w.currency}
                              </span>
                            </p>
                            {w.status === 'rejected' && (
                              <div className="mt-2 flex items-start gap-1.5 bg-red-50 dark:bg-red-900/20 rounded-md px-2.5 py-2">
                                <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                                <p className="text-red-600 dark:text-red-400">Funds have been returned to the trader's balance.</p>
                              </div>
                            )}
                            {w.rejection_note && (
                              <p className="mt-1 italic text-red-600 dark:text-red-400">Reason: {w.rejection_note}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      {canAct && !isAction && (
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[var(--border-color)]">
                          <button onClick={() => openAction(w.id, 'approve')} className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs flex items-center gap-1 transition-smooth">
                            <CheckCircle className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button onClick={() => openAction(w.id, 'reject')} className="px-4 py-2 border border-red-300 dark:border-red-700 rounded-lg text-xs flex items-center gap-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-smooth">
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                      {isAction && (
                        <div className={`mt-4 pt-3 border-t ${actionType === 'approve' ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'}`}>
                          <p className={`text-xs font-medium mb-2 ${actionType === 'approve' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                            {actionType === 'approve'
                              ? "Confirm withdrawal approval — funds will be released to the trader's payout details."
                              : "Confirm rejection — the locked amount will be returned to the trader's balance."}
                          </p>
                          {actionType === 'reject' && (
                            <textarea value={note} onChange={e => setNote(e.target.value)}
                              placeholder="Reason for rejection (optional)…" rows={2}
                              className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs mb-2 focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)]" />
                          )}
                          <div className="flex items-center gap-2">
                            <button onClick={submitAction} disabled={submitting}
                              className={`px-4 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-50 transition-smooth ${actionType === 'approve' ? 'bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-dark)] text-white' : 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white'}`}>
                              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (actionType === 'approve' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />)}
                              {submitting ? 'Processing…' : (actionType === 'approve' ? 'Approve' : 'Reject')}
                            </button>
                            <button onClick={cancelAction} className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}